import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, AlertCircle, RefreshCw, Info } from 'lucide-react';
import { macroAPI } from '../services/api';
import { MetricTooltip } from './MetricTooltip';

// Market code to country code mapping
const MARKET_TO_COUNTRY = {
  'VN': 'VN',
  'NSE': 'IN',
  'PSE': 'PH',
  'JK': 'ID',
  'WSE': 'PL',
  'KRX': 'KR',
  // Direct country codes
  'IN': 'IN',
  'PH': 'PH',
  'ID': 'ID',
  'PL': 'PL',
  'KR': 'KR'
};

const MacroWidgetExtended = ({ countryCode, countryName }) => {
  const [trends, setTrends] = useState(null);
  const [dataQuality, setDataQuality] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Convert market code to country code
  const effectiveCountryCode = MARKET_TO_COUNTRY[countryCode] || countryCode;

  useEffect(() => {
    fetchData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [effectiveCountryCode]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await macroAPI.getTrends(effectiveCountryCode);
      setTrends(data.trends);
      setDataQuality(data.data_quality);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch macro trends:', err);
      setError('Données indisponibles');
    } finally {
      setLoading(false);
    }
  };

  const getDirectionIcon = (direction) => {
    switch (direction) {
      case 'accelerating':
      case 'rising':
        return <TrendingUp className="w-4 h-4" />;
      case 'decelerating':
      case 'falling':
        return <TrendingDown className="w-4 h-4" />;
      default:
        return <Minus className="w-4 h-4" />;
    }
  };

  const getVerdictColor = (status) => {
    switch (status) {
      case 'favorable':
        return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'prudence':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'mixed':
      case 'slowdown':
      case 'neutral':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getTrendColor = (badge) => {
    if (badge === '🟢') return 'text-emerald-400';
    if (badge === '🔴') return 'text-red-400';
    return 'text-amber-400';
  };

  const getConfidenceColor = (level) => {
    switch (level) {
      case 'high':
        return 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400';
      case 'medium':
        return 'bg-amber-500/10 border-amber-500/30 text-amber-400';
      default:
        return 'bg-gray-500/10 border-gray-500/30 text-gray-400';
    }
  };

  const getSourceBadge = (source) => {
    if (!source) return { icon: '📊', label: 'N/A', color: 'text-gray-400' };
    
    const sourceUpper = source.toUpperCase();
    if (sourceUpper.includes('OECD')) {
      return { icon: '✅', label: source, color: 'text-emerald-400' };
    }
    if (sourceUpper.includes('GUS') || sourceUpper.includes('TRADING')) {
      return { icon: '✅', label: source, color: 'text-emerald-400' };
    }
    if (sourceUpper.includes('BANK OF KOREA') || sourceUpper.includes('KOSTAT')) {
      return { icon: '✅', label: source, color: 'text-emerald-400' };
    }
    if (sourceUpper.includes('RBI') || sourceUpper.includes('BPS') || sourceUpper.includes('GSO') || sourceUpper.includes('PSA')) {
      return { icon: '✅', label: source, color: 'text-emerald-400' };
    }
    if (sourceUpper.includes('WORLD BANK')) {
      return { icon: '📊', label: 'World Bank', color: 'text-amber-400' };
    }
    return { icon: '📊', label: source, color: 'text-gray-400' };
  };

  if (loading && !trends) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-4 animate-pulse">
        <div className="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
        <div className="h-20 bg-white/10 rounded"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        <div className="flex items-center gap-2 text-gray-400">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
          <button onClick={fetchData} className="ml-auto hover:text-white">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  if (!trends) return null;

  const verdict = trends.combined_verdict || {};
  const gdpTrend = trends.gdp || {};
  const inflationTrend = trends.inflation || {};

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid="macro-widget">
      {/* Header with verdict */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-white font-medium">{countryName}</h3>
          <span className="text-lg">{verdict.badge}</span>
        </div>
        <MetricTooltip metric="combinedVerdict">
          <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getVerdictColor(verdict.status)}`}>
            {verdict.label}
          </div>
        </MetricTooltip>
      </div>

      {/* Data quality indicator */}
      {dataQuality && (
        <div className={`mb-4 px-3 py-2 rounded-lg border text-xs ${getConfidenceColor(dataQuality.confidence_level)}`}>
          <MetricTooltip metric="dataConfidence">
            <div className="flex items-center gap-2">
              <Info className="w-3 h-3" />
              <span>
                Confiance: {dataQuality.confidence_level === 'high' ? 'Élevée' : dataQuality.confidence_level === 'medium' ? 'Moyenne' : 'Faible'}
              </span>
            </div>
          </MetricTooltip>
        </div>
      )}

      {/* GDP Trend */}
      <div className="mb-3 p-3 bg-black/20 rounded-lg">
        <div className="flex items-center justify-between mb-1">
          <MetricTooltip metric="gdpTrend">
            <div className="flex items-center gap-2">
              <span className="text-lg">{gdpTrend.badge}</span>
              <span className="text-sm text-gray-300">Croissance PIB</span>
            </div>
          </MetricTooltip>
          <span className={`text-sm font-medium ${getTrendColor(gdpTrend.badge)}`}>
            {gdpTrend.label}
          </span>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {gdpTrend.recent_values}
        </div>
        {dataQuality && (
          <MetricTooltip metric={dataQuality.gdp_source === 'OECD' ? 'dataSourceOECD' : 'dataSourceWorldBank'}>
            <div className={`text-xs mt-1 ${getSourceBadge(dataQuality.gdp_source).color}`}>
              {getSourceBadge(dataQuality.gdp_source).icon} {getSourceBadge(dataQuality.gdp_source).label}
              {dataQuality.gdp_granularity === 'quarterly' ? ' (trim.)' : ' (annuel)'}
            </div>
          </MetricTooltip>
        )}
      </div>

      {/* Inflation Trend */}
      <div className="mb-3 p-3 bg-black/20 rounded-lg">
        <div className="flex items-center justify-between mb-1">
          <MetricTooltip metric="inflationTrend">
            <div className="flex items-center gap-2">
              <span className="text-lg">{inflationTrend.badge}</span>
              <span className="text-sm text-gray-300">Inflation</span>
            </div>
          </MetricTooltip>
          <span className={`text-sm font-medium ${getTrendColor(inflationTrend.badge)}`}>
            {inflationTrend.label}
          </span>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {inflationTrend.recent_values}
        </div>
        {dataQuality && (
          <MetricTooltip metric={dataQuality.inflation_source === 'OECD' ? 'dataSourceOECD' : 'dataSourceWorldBank'}>
            <div className={`text-xs mt-1 ${getSourceBadge(dataQuality.inflation_source).color}`}>
              {getSourceBadge(dataQuality.inflation_source).icon} {getSourceBadge(dataQuality.inflation_source).label}
              {dataQuality.inflation_granularity === 'monthly' ? ' (mens.)' : ' (annuel)'}
            </div>
          </MetricTooltip>
        )}
      </div>

      {/* Verdict description */}
      <MetricTooltip metric={verdict.status === 'favorable' ? 'verdictFavorable' : verdict.status === 'prudence' ? 'verdictPrudence' : 'verdictNeutral'}>
        <div className="text-xs text-gray-500 text-center mt-3 pt-3 border-t border-white/5">
          {verdict.description}
        </div>
      </MetricTooltip>
    </div>
  );
};

export default MacroWidgetExtended;
